/**
 * Last-known-good feature flags, kept in localStorage so a slow flags request
 * on a later visit can fall back to real values instead of all-false.
 *
 * Flags are per stage, not per user, so a value written in one session is
 * valid for any user of the same API endpoint until it ages out.
 */

export const PERSISTED_FLAGS_STORAGE_KEY = "claimIntake.featureFlags.lastKnownGood";

/** Long enough to cover a working day between visits, short enough that a flag flip lands by the next one. */
export const PERSISTED_FLAGS_MAX_AGE_MS = 24 * 60 * 60 * 1000;

interface PersistedFlagsEntry {
  endpoint: string;
  fetchedAt: number;
  flags: Record<string, boolean>;
}

const isBooleanRecord = (value: unknown): value is Record<string, boolean> =>
  typeof value === "object" &&
  value !== null &&
  !Array.isArray(value) &&
  Object.values(value).every((flag) => typeof flag === "boolean");

const isPersistedFlagsEntry = (value: unknown): value is PersistedFlagsEntry => {
  if (typeof value !== "object" || value === null) return false;
  const entry = value as Record<string, unknown>;
  return (
    typeof entry.endpoint === "string" &&
    typeof entry.fetchedAt === "number" &&
    isBooleanRecord(entry.flags)
  );
};

/**
 * Returns the persisted flags when they were written for this endpoint and are
 * still within the max age; otherwise null.
 */
export const readPersistedFlags = (
  endpoint: string,
  now: number = Date.now()
): Record<string, boolean> | null => {
  try {
    const raw = window.localStorage.getItem(PERSISTED_FLAGS_STORAGE_KEY);
    if (!raw) return null;

    const entry: unknown = JSON.parse(raw);
    if (!isPersistedFlagsEntry(entry)) return null;
    if (entry.endpoint !== endpoint) return null;

    const age = now - entry.fetchedAt;
    // A negative age means the clock moved backwards; the entry's age is unknowable.
    if (age < 0 || age > PERSISTED_FLAGS_MAX_AGE_MS) return null;

    return entry.flags;
  } catch {
    // Storage disabled, or a corrupt entry: both mean "no fallback available".
    return null;
  }
};

export const writePersistedFlags = (
  endpoint: string,
  flags: Record<string, boolean>,
  now: number = Date.now()
): void => {
  const entry: PersistedFlagsEntry = { endpoint, fetchedAt: now, flags };
  try {
    window.localStorage.setItem(PERSISTED_FLAGS_STORAGE_KEY, JSON.stringify(entry));
  } catch {
    // Quota or privacy-mode failures only cost the fallback, never the live value.
  }
};
